"use client"

import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import React, { useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { transitionDelayTime, useInViewConfig, useTransitionUpStyles } from "ui"
import ButtonWithArrow from "ui/common/bit/ButtonWithArrow"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { getNonUmbracoUrl, getPreviewTextFromRichText } from "utils"

const StyledTextsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${colors.black};
  gap: 10px;
`
const StyledSubtitle = styled(Typography)<{ component?: string }>`
  text-align: start;
  max-width: 100%;
  color: ${colors.red};
  ${mediaQueries.mobile} {
    font-size: 19px;
  }
`
const StyledTitle = styled(Typography)<{ component?: React.ElementType }>`
  text-align: start;
  margin-bottom: 40px;
  color: ${colors.black};
  ${mediaQueries.tablet} {
    margin-bottom: 24px;
  }
  ${mediaQueries.mobile} {
    margin-bottom: 24px;
    font-size: 31px;
  }
`
const StyledText = styled(Typography)<{ component?: string }>`
  text-align: start;
  color: inherit;
  ${mediaQueries.mobile} {
    font-size: 15px;
    line-height: 18px;
  }
  ${mediaQueries.tablet} {
    font-size: 18px;
    line-height: 24.65px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 18px;
    line-height: 27px;
  }
  ul {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    list-style-type: none;
    li {
      position: relative;
      display: flex;
      flex-flow: row wrap;
      align-items: flex-start;
      color: ${colors.black};
      a {
        color: ${colors.black};
      }
      &::before {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        height: 100%;
        content: url("/icons/check.svg");
        margin-right: ${sizes.baseSingle};
      }
    }
  }
`
const StyledReadMoreButton = styled("button")<{
  as?: any
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 40px;
  color: ${colors.black};
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    text-decoration: underline;
    color: ${colors.red};
  }
  svg {
    transform: rotate(-90deg);
    path {
      stroke: ${colors.black};
    }
  }
`

const StyledFullTextContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{
  expanded: boolean
}>`
  transition: all 0.4s ease-in-out;
  max-height: ${({ expanded }) => (expanded ? "1000px" : "0")};
  overflow: hidden;
  opacity: ${({ expanded }) => (expanded ? 1 : 0)};
  pointer-events: ${({ expanded }) => (expanded ? "auto" : "none")};
`
const StyledButtonWithArrow = styled(ButtonWithArrow)`
  height: 56px;
`
const StyledButtonWrapper = styled(Box)`
  display: flex;
  gap: ${sizes.baseDouble};
  margin-top: 30px;
`

export type CommunityTextsProps = {
  data: {
    title?: { value: string }
    subtitle?: { value: string } | string
    text?: { richText: string } | string
    hasExpandButton?: boolean
    button?: { links: { url: string; name: string }[] }
    buttonPrevious?: { links: { url: string; name: string }[] }
    additionalButton?: { links: { url: string; name: string }[] }
    ArrowIcon?: React.ElementType
  }
  children?: React.ReactNode
  className?: string
  onButtonClick?: () => void
  onAdditionalButtonClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  titleComponent?: React.ElementType
}

const CommunityTexts: React.FC<CommunityTextsProps> = ({
  data = {},
  children,
  className,
  onButtonClick,
  onAdditionalButtonClick,
  titleComponent,
}) => {
  const {
    title,
    subtitle,
    text,
    hasExpandButton,
    button,
    buttonPrevious,
    additionalButton,
    ArrowIcon,
  } = data

  const { ref, inView } = useInView(useInViewConfig)
  const ditransitiveStyles = useTransitionUpStyles(inView, transitionDelayTime)

  // Collapsible state for text
  const [expanded, setExpanded] = useState(false)
  const fullTextRef = useRef<HTMLDivElement>(null)

  const t = useTranslations("Common")

  // Handler for "read more" link click
  const handleExpandClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setExpanded(true)
    setTimeout(() => {
      fullTextRef.current?.focus()
    }, 200)
  }
  return (
    <StyledTextsContainer
      ref={ref}
      style={{ ...ditransitiveStyles }}
      className={className}
    >
      {subtitle && (
        <StyledSubtitle className="subtitle" variant="h3" component="p">
          {typeof subtitle === "object" && subtitle.value
            ? subtitle.value
            : typeof subtitle === "string"
              ? subtitle
              : ""}
        </StyledSubtitle>
      )}
      {(title || children) && (
        <StyledTitle
          className="title"
          variant="h2"
          component={titleComponent || undefined}
        >
          {title?.value ? title.value : children}
        </StyledTitle>
      )}
      {/* Collapsible text logic */}
      {hasExpandButton ? (
        <>
          <StyledText
            className="text"
            variant="body2"
            sx={{
              display: expanded ? "none" : "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            aria-hidden={expanded}
          >
            {getPreviewTextFromRichText(text)}
          </StyledText>
          <StyledFullTextContainer
            tabIndex={expanded ? 0 : -1}
            ref={fullTextRef}
            expanded={expanded}
            aria-hidden={!expanded}
            aria-label="Full text"
          >
            <StyledText
              className="text"
              variant="body2"
              component="div"
              tabIndex={-1}
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(
                  typeof text === "object" && "richText" in text
                    ? text.richText
                    : (text as string),
                ),
              }}
            />
          </StyledFullTextContainer>
          {!expanded && (
            <StyledReadMoreButton
              type="button"
              onClick={handleExpandClick}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              aria-controls="community-full-text"
            >
              {t("readMore")}
              {ArrowIcon && <ArrowIcon />}
            </StyledReadMoreButton>
          )}
        </>
      ) : text ? (
        <StyledText
          className="text"
          variant="body3"
          component="div"
          dangerouslySetInnerHTML={{
            __html: replaceRichTextLinks(
              typeof text === "object" && "richText" in text
                ? text.richText
                : (text as string),
            ),
          }}
        />
      ) : (
        <StyledText className="text" variant="body2"></StyledText>
      )}
      {(button || buttonPrevious || additionalButton) && (
        <StyledButtonWrapper>
          {button && button.links?.[0] && (
            <StyledButtonWithArrow
              href={getNonUmbracoUrl(button.links?.[0]?.url)}
              onClick={onButtonClick}
            >
              <Typography variant="CTA" color={colors.white}>
                {button.links[0]?.name}
              </Typography>
            </StyledButtonWithArrow>
          )}
          {buttonPrevious && buttonPrevious?.links && (
            <StyledButtonWithArrow
              arrowName="arrow_blue_white_line"
              href={getNonUmbracoUrl(buttonPrevious?.links?.[0]?.url)}
            >
              <Typography variant="CTA" color={colors.white}>
                {buttonPrevious.links[0]?.name}
              </Typography>
            </StyledButtonWithArrow>
          )}
          {additionalButton && additionalButton?.links?.[0] && (
            <StyledButtonWithArrow
              variant="elevated"
              colorPallette="blue"
              href={getNonUmbracoUrl(additionalButton?.links[0].url)}
              onClick={onAdditionalButtonClick}
            >
              <Typography variant="CTA">
                {additionalButton.links[0]?.name}
              </Typography>
            </StyledButtonWithArrow>
          )}
        </StyledButtonWrapper>
      )}
    </StyledTextsContainer>
  )
}

export default CommunityTexts
